// API base URL — set REACT_APP_API_URL in your .env to your API Gateway URL
// e.g. REACT_APP_API_URL=https://abc123.execute-api.ap-south-1.amazonaws.com
const API_BASE = process.env.REACT_APP_API_URL || '';

// Admin credentials (kept in env — move to Cognito for production)
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'admin@romasdentalcare.com';
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'Admin@123';

// ── HTTP helpers ──────────────────────────────────────────────────────────────

const apiFetch = async (path, options = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  return res.json();
};

const apiGet = (path) => apiFetch(path);
const apiPost = (path, body) => apiFetch(path, { method: 'POST', body: JSON.stringify(body) });
const apiPut = (path, body) => apiFetch(path, { method: 'PUT', body: JSON.stringify(body) });
const apiDelete = (path) => apiFetch(path, { method: 'DELETE' });

/**
 * Upload a file via S3 pre-signed URL (no AWS credentials needed in browser).
 * 1. Ask Lambda for a pre-signed PUT URL
 * 2. PUT the file bytes directly to S3
 * Returns { publicUrl, key } on success.
 */
const uploadFileViaPresignedUrl = async (file, folder) => {
  const urlRes = await apiPost('/upload-url', {
    fileName: file.name,
    contentType: file.type,
    folder,
  });

  if (!urlRes.success) throw new Error(urlRes.message || 'Failed to get upload URL');

  await fetch(urlRes.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });

  return { publicUrl: urlRes.publicUrl, key: urlRes.key };
};

// ── Auth API ──────────────────────────────────────────────────────────────────

export const authAPI = {
  login: async (email, password) => {
    try {
      if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
        const adminData = { email, name: 'Admin' };
        localStorage.setItem('adminToken', 'logged-in');
        localStorage.setItem('adminData', JSON.stringify(adminData));
        return { success: true, admin: adminData };
      }
      return { success: false, message: 'Invalid credentials' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed' };
    }
  },

  logout: () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
  },

  isAuthenticated: () => !!localStorage.getItem('adminToken'),

  getAdmin: () => {
    const data = localStorage.getItem('adminData');
    return data ? JSON.parse(data) : null;
  },
};

// ── Bookings API ──────────────────────────────────────────────────────────────

export const bookingsAPI = {
  getAll: async () => {
    try {
      return await apiGet('/bookings');
    } catch (error) {
      console.error('Get bookings error:', error);
      return { success: false, message: 'Failed to fetch bookings' };
    }
  },

  create: async (bookingData) => {
    try {
      return await apiPost('/bookings', bookingData);
    } catch (error) {
      console.error('Create booking error:', error);
      return { success: false, message: 'Failed to create booking' };
    }
  },

  updateStatus: async (id, status) => {
    try {
      const result = await apiPut(`/bookings/${id}/status`, { status });

      if (status === 'confirmed' && result.success && result.booking) {
        const booking = result.booking;
        const message =
          `Hello ${booking.name},\n\nThank you for booking an appointment with Roma's Dental Care!\n\n` +
          `Your Appointment has been *CONFIRMED* ✅\n\n` +
          `*Booking ID:* #${booking.bookingNumber}\n*Service:* ${booking.service}\n` +
          `*Date:* ${new Date(booking.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}\n` +
          `*Time:* ${booking.time}\n\n` +
          `*Clinic Address:*\nShop no. 7, Society Complex, SHUBH SHAGUN,\nOld Mundhwa Rd, opposite Bollywood Multiplex,\nTukaram Nagar, Kharadi, Pune - 411014\n\n` +
          `We look forward to serving you!\n\n- Roma's Dental Care Team`;

        if (typeof window !== 'undefined') {
          window.open(`https://wa.me/91${booking.mobile}?text=${encodeURIComponent(message)}`, '_blank');
        }
      }

      return result;
    } catch (error) {
      console.error('Update booking error:', error);
      return { success: false, message: 'Failed to update booking' };
    }
  },

  delete: async (id) => {
    try {
      return await apiDelete(`/bookings/${id}`);
    } catch (error) {
      console.error('Delete booking error:', error);
      return { success: false, message: 'Failed to delete booking' };
    }
  },
};

// ── Services API ──────────────────────────────────────────────────────────────

export const servicesAPI = {
  getAll: async () => {
    try {
      return await apiGet('/services');
    } catch (error) {
      console.error('Get services error:', error);
      return { success: false, message: 'Failed to fetch services' };
    }
  },

  create: async (serviceData) => {
    try {
      return await apiPost('/services', serviceData);
    } catch (error) {
      console.error('Create service error:', error);
      return { success: false, message: 'Failed to create service' };
    }
  },

  update: async (id, serviceData) => {
    try {
      return await apiPut(`/services/${id}`, serviceData);
    } catch (error) {
      console.error('Update service error:', error);
      return { success: false, message: 'Failed to update service' };
    }
  },

  delete: async (id) => {
    try {
      return await apiDelete(`/services/${id}`);
    } catch (error) {
      console.error('Delete service error:', error);
      return { success: false, message: 'Failed to delete service' };
    }
  },
};

// ── Gallery API ───────────────────────────────────────────────────────────────

export const galleryAPI = {
  getAll: async () => {
    try {
      return await apiGet('/gallery');
    } catch (error) {
      console.error('Get gallery error:', error);
      return { success: false, message: 'Failed to fetch gallery' };
    }
  },

  upload: async (file, title, description, category) => {
    try {
      const { publicUrl, key } = await uploadFileViaPresignedUrl(file, 'gallery');
      return await apiPost('/gallery', { title, description, category, imageUrl: publicUrl, s3Key: key });
    } catch (error) {
      console.error('Upload error:', error);
      return { success: false, message: 'Failed to upload image' };
    }
  },

  update: async (id, updateData) => {
    try {
      return await apiPut(`/gallery/${id}`, updateData);
    } catch (error) {
      console.error('Update image error:', error);
      return { success: false, message: 'Failed to update image' };
    }
  },

  delete: async (id) => {
    try {
      return await apiDelete(`/gallery/${id}`);
    } catch (error) {
      console.error('Delete image error:', error);
      return { success: false, message: 'Failed to delete image' };
    }
  },
};

// ── Blogs API ─────────────────────────────────────────────────────────────────

export const blogsAPI = {
  getAll: async () => {
    try {
      return await apiGet('/blogs');
    } catch (error) {
      console.error('Get blogs error:', error);
      return { success: false, message: 'Failed to fetch blogs' };
    }
  },

  getOne: async (id) => {
    try {
      return await apiGet(`/blogs/${id}`);
    } catch (error) {
      console.error('Get blog error:', error);
      return { success: false, message: 'Failed to fetch blog' };
    }
  },

  create: async (blogData, imageFile) => {
    try {
      let imageUrl = '';
      let s3Key = '';
      if (imageFile) {
        const uploaded = await uploadFileViaPresignedUrl(imageFile, 'blogs');
        imageUrl = uploaded.publicUrl;
        s3Key = uploaded.key;
      }
      return await apiPost('/blogs', { ...blogData, imageUrl, s3Key });
    } catch (error) {
      console.error('Create blog error:', error);
      return { success: false, message: 'Failed to create blog' };
    }
  },

  update: async (id, blogData, imageFile) => {
    try {
      let payload = { ...blogData };
      if (imageFile) {
        const uploaded = await uploadFileViaPresignedUrl(imageFile, 'blogs');
        payload.imageUrl = uploaded.publicUrl;
        payload.s3Key = uploaded.key;
      }
      return await apiPut(`/blogs/${id}`, payload);
    } catch (error) {
      console.error('Update blog error:', error);
      return { success: false, message: 'Failed to update blog' };
    }
  },

  delete: async (id) => {
    try {
      return await apiDelete(`/blogs/${id}`);
    } catch (error) {
      console.error('Delete blog error:', error);
      return { success: false, message: 'Failed to delete blog' };
    }
  },
};

// ── Home Content API ──────────────────────────────────────────────────────────

export const homeContentAPI = {
  get: async () => {
    try {
      return await apiGet('/home-content');
    } catch (error) {
      console.error('Get home content error:', error);
      return { success: false, message: 'Failed to fetch home content' };
    }
  },

  update: async (contentData, imageFile = null) => {
    try {
      let payload = { ...contentData };
      if (imageFile) {
        const uploaded = await uploadFileViaPresignedUrl(imageFile, 'home-content');
        payload.heroImage = uploaded.publicUrl;
        payload.s3Key = uploaded.key;
      }
      return await apiPut('/home-content', payload);
    } catch (error) {
      console.error('Update home content error:', error);
      return { success: false, message: 'Failed to update home content' };
    }
  },
};

// ── About Content API ─────────────────────────────────────────────────────────

export const aboutContentAPI = {
  get: async () => {
    try {
      return await apiGet('/about-content');
    } catch (error) {
      console.error('Get about content error:', error);
      return { success: false, message: 'Failed to fetch about content' };
    }
  },

  update: async (contentData, imageFile = null) => {
    try {
      let payload = { ...contentData };
      if (imageFile) {
        const uploaded = await uploadFileViaPresignedUrl(imageFile, 'about-content');
        payload.mainImage = uploaded.publicUrl;
        payload.s3Key = uploaded.key;
      }
      return await apiPut('/about-content', payload);
    } catch (error) {
      console.error('Update about content error:', error);
      return { success: false, message: 'Failed to update about content' };
    }
  },
};

// ── Stats API ─────────────────────────────────────────────────────────────────

export const statsAPI = {
  get: async () => {
    try {
      return await apiGet('/stats');
    } catch (error) {
      console.error('Get stats error:', error);
      return { success: false, message: 'Failed to fetch stats' };
    }
  },

  update: async (statsData) => {
    try {
      return await apiPut('/stats', statsData);
    } catch (error) {
      console.error('Update stats error:', error);
      return { success: false, message: 'Failed to update stats' };
    }
  },
};

// ── FAQ API ───────────────────────────────────────────────────────────────────

export const faqAPI = {
  getAll: async () => {
    try {
      return await apiGet('/faqs');
    } catch (error) {
      console.error('Get FAQs error:', error);
      return { success: false, message: 'Failed to fetch FAQs', faqs: [] };
    }
  },

  create: async (faqData) => {
    try {
      return await apiPost('/faqs', faqData);
    } catch (error) {
      console.error('Create FAQ error:', error);
      return { success: false, message: 'Failed to add FAQ' };
    }
  },

  update: async (faqId, faqData) => {
    try {
      return await apiPut(`/faqs/${faqId}`, faqData);
    } catch (error) {
      console.error('Update FAQ error:', error);
      return { success: false, message: 'Failed to update FAQ' };
    }
  },

  delete: async (faqId) => {
    try {
      return await apiDelete(`/faqs/${faqId}`);
    } catch (error) {
      console.error('Delete FAQ error:', error);
      return { success: false, message: 'Failed to delete FAQ' };
    }
  },
};

// ── Social Links API ──────────────────────────────────────────────────────────

export const socialLinksAPI = {
  getAll: async () => {
    try {
      return await apiGet('/social-links');
    } catch (error) {
      console.error('Get social links error:', error);
      return { success: false, message: 'Failed to fetch social links', socialLinks: [] };
    }
  },

  create: async (linkData) => {
    try {
      return await apiPost('/social-links', linkData);
    } catch (error) {
      console.error('Create social link error:', error);
      return { success: false, message: 'Failed to add social link' };
    }
  },

  update: async (linkId, linkData) => {
    try {
      return await apiPut(`/social-links/${linkId}`, linkData);
    } catch (error) {
      console.error('Update social link error:', error);
      return { success: false, message: 'Failed to update social link' };
    }
  },

  delete: async (linkId) => {
    try {
      return await apiDelete(`/social-links/${linkId}`);
    } catch (error) {
      console.error('Delete social link error:', error);
      return { success: false, message: 'Failed to delete social link' };
    }
  },
};
